/* ========================================
   Smokey's Wallet — Custom Element
   <smokeys-wallet> with bundled QR generator
   ======================================== */

// ── Config flags ──
var SMK_WALLET_CONFIG = window.SMK_WALLET_CONFIG || {};
var SMK_ENABLE_GOOGLE_WALLET = SMK_WALLET_CONFIG.enableGoogleWallet || false;

// ── Minimal QR Code Generator (bundled, no external deps) ──
// Generates QR codes as canvas. Supports alphanumeric + byte mode, EC level M.
const QR = (() => {
  // Galois field GF(256) with primitive polynomial 0x11D
  const EXP = new Uint8Array(256);
  const LOG = new Uint8Array(256);
  let x = 1;
  for (let i = 0; i < 255; i++) { EXP[i] = x; LOG[x] = i; x = (x << 1) ^ (x & 128 ? 0x11D : 0); }
  EXP[255] = EXP[0];

  function gfMul(a, b) { return a === 0 || b === 0 ? 0 : EXP[(LOG[a] + LOG[b]) % 255]; }

  function polyMul(a, b) {
    const r = new Uint8Array(a.length + b.length - 1);
    for (let i = 0; i < a.length; i++)
      for (let j = 0; j < b.length; j++)
        r[i + j] ^= gfMul(a[i], b[j]);
    return r;
  }

  function polyRemainder(data, gen) {
    const r = new Uint8Array(data.length + gen.length - 1);
    r.set(data);
    for (let i = 0; i < data.length; i++) {
      if (r[i] === 0) continue;
      for (let j = 0; j < gen.length; j++)
        r[i + j] ^= gfMul(gen[j], r[i]);
    }
    return r.slice(data.length);
  }

  function generatorPoly(n) {
    let g = new Uint8Array([1]);
    for (let i = 0; i < n; i++)
      g = polyMul(g, new Uint8Array([1, EXP[i]]));
    return g;
  }

  const VERSION_INFO = [
    null,
    { total: 26, dataCodewords: 16, ecPerBlock: 10, blocks: 1 },
    { total: 44, dataCodewords: 28, ecPerBlock: 16, blocks: 1 },
    { total: 70, dataCodewords: 44, ecPerBlock: 26, blocks: 1 },
    { total: 100, dataCodewords: 64, ecPerBlock: 18, blocks: 2 },
    { total: 134, dataCodewords: 86, ecPerBlock: 24, blocks: 2 },
    { total: 172, dataCodewords: 108, ecPerBlock: 16, blocks: 4 },
    { total: 196, dataCodewords: 124, ecPerBlock: 18, blocks: 4 },
    { total: 242, dataCodewords: 154, ecPerBlock: 22, blocks: 4 },
    { total: 292, dataCodewords: 182, ecPerBlock: 22, blocks: 5 },
    { total: 346, dataCodewords: 216, ecPerBlock: 26, blocks: 5 },
  ];

  const BYTE_CAPACITY = [0, 14, 26, 42, 62, 84, 106, 122, 152, 180, 213];

  function selectVersion(dataLen) {
    for (let v = 1; v <= 10; v++) {
      if (dataLen <= BYTE_CAPACITY[v]) return v;
    }
    throw new Error('Data too long for QR (max ~213 bytes)');
  }

  function encodeData(str, version) {
    const info = VERSION_INFO[version];
    const bits = [];

    function push(val, len) {
      for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1);
    }

    push(0b0100, 4);
    const ccBits = version <= 9 ? 8 : 16;
    push(str.length, ccBits);

    for (let i = 0; i < str.length; i++) push(str.charCodeAt(i), 8);

    const capacity = info.dataCodewords * 8;
    const termLen = Math.min(4, capacity - bits.length);
    for (let i = 0; i < termLen; i++) bits.push(0);

    while (bits.length % 8 !== 0) bits.push(0);

    const padBytes = [0xEC, 0x11];
    let padIdx = 0;
    while (bits.length < capacity) {
      push(padBytes[padIdx % 2], 8);
      padIdx++;
    }

    const data = new Uint8Array(info.dataCodewords);
    for (let i = 0; i < data.length; i++) {
      let byte = 0;
      for (let b = 0; b < 8; b++) byte = (byte << 1) | bits[i * 8 + b];
      data[i] = byte;
    }

    return data;
  }

  function addErrorCorrection(data, version) {
    const info = VERSION_INFO[version];
    const ecLen = info.ecPerBlock;
    const gen = generatorPoly(ecLen);
    const blockSize = Math.floor(info.dataCodewords / info.blocks);
    const longBlocks = info.dataCodewords % info.blocks;

    const dataBlocks = [];
    const ecBlocks = [];
    let offset = 0;

    for (let b = 0; b < info.blocks; b++) {
      const bLen = blockSize + (b >= info.blocks - longBlocks ? 1 : 0);
      const block = data.slice(offset, offset + bLen);
      dataBlocks.push(block);
      ecBlocks.push(polyRemainder(block, gen));
      offset += bLen;
    }

    const result = [];
    const maxDataLen = blockSize + (longBlocks > 0 ? 1 : 0);
    for (let i = 0; i < maxDataLen; i++)
      for (let b = 0; b < info.blocks; b++)
        if (i < dataBlocks[b].length) result.push(dataBlocks[b][i]);
    for (let i = 0; i < ecLen; i++)
      for (let b = 0; b < info.blocks; b++)
        result.push(ecBlocks[b][i]);

    return result;
  }

  function createMatrix(version) {
    const size = version * 4 + 17;
    const matrix = Array.from({ length: size }, () => new Int8Array(size));
    const reserved = Array.from({ length: size }, () => new Uint8Array(size));

    function setModule(r, c, val) {
      if (r >= 0 && r < size && c >= 0 && c < size) {
        matrix[r][c] = val ? 1 : -1;
        reserved[r][c] = 1;
      }
    }

    function finderPattern(row, col) {
      for (let r = -1; r <= 7; r++)
        for (let c = -1; c <= 7; c++) {
          const rr = row + r, cc = col + c;
          if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
          const isBlack = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
                          (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
                          (r >= 2 && r <= 4 && c >= 2 && c <= 4);
          setModule(rr, cc, isBlack);
        }
    }

    finderPattern(0, 0);
    finderPattern(0, size - 7);
    finderPattern(size - 7, 0);

    for (let i = 8; i < size - 8; i++) {
      setModule(6, i, i % 2 === 0);
      setModule(i, 6, i % 2 === 0);
    }

    setModule(size - 8, 8, true);

    if (version >= 2) {
      const positions = alignmentPositions(version);
      for (const r of positions)
        for (const c of positions) {
          if (reserved[r][c]) continue;
          for (let dr = -2; dr <= 2; dr++)
            for (let dc = -2; dc <= 2; dc++) {
              const isBlack = Math.abs(dr) === 2 || Math.abs(dc) === 2 || (dr === 0 && dc === 0);
              setModule(r + dr, c + dc, isBlack);
            }
        }
    }

    for (let i = 0; i < 8; i++) {
      if (!reserved[8][i]) { reserved[8][i] = 1; matrix[8][i] = 0; }
      if (!reserved[8][size - 1 - i]) { reserved[8][size - 1 - i] = 1; matrix[8][size - 1 - i] = 0; }
      if (!reserved[i][8]) { reserved[i][8] = 1; matrix[i][8] = 0; }
      if (!reserved[size - 1 - i][8]) { reserved[size - 1 - i][8] = 1; matrix[size - 1 - i][8] = 0; }
    }
    if (!reserved[8][8]) { reserved[8][8] = 1; matrix[8][8] = 0; }

    return { matrix, reserved, size };
  }

  function alignmentPositions(version) {
    if (version === 1) return [];
    const positions = [6];
    const last = version * 4 + 10;
    const count = Math.floor(version / 7) + 2;
    const step = count === 2 ? last - 6 : Math.ceil((last - 6) / (count - 1));
    for (let i = 1; i < count; i++) positions.push(6 + i * step);
    positions[positions.length - 1] = last;
    return positions;
  }

  function placeData(matrix, reserved, size, bits) {
    let bitIdx = 0;
    let upward = true;

    for (let right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      const rows = upward ? Array.from({ length: size }, (_, i) => size - 1 - i) : Array.from({ length: size }, (_, i) => i);

      for (const row of rows) {
        for (const col of [right, right - 1]) {
          if (reserved[row][col]) continue;
          if (bitIdx < bits.length) {
            matrix[row][col] = bits[bitIdx] ? 1 : -1;
          } else {
            matrix[row][col] = -1;
          }
          bitIdx++;
        }
      }
      upward = !upward;
    }
  }

  const MASK_FNS = [
    (r, c) => (r + c) % 2 === 0,
    (r, c) => r % 2 === 0,
    (r, c) => c % 3 === 0,
    (r, c) => (r + c) % 3 === 0,
    (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
    (r, c) => (r * c) % 2 + (r * c) % 3 === 0,
    (r, c) => ((r * c) % 2 + (r * c) % 3) % 2 === 0,
    (r, c) => ((r + c) % 2 + (r * c) % 3) % 2 === 0,
  ];

  function applyMask(matrix, reserved, size, maskIdx) {
    const fn = MASK_FNS[maskIdx];
    const result = matrix.map(row => Int8Array.from(row));
    for (let r = 0; r < size; r++)
      for (let c = 0; c < size; c++)
        if (!reserved[r][c] && fn(r, c))
          result[r][c] = result[r][c] === 1 ? -1 : 1;
    return result;
  }

  const FORMAT_BITS = [
    0x5412, 0x5125, 0x5E7C, 0x5B4B, 0x45F9, 0x40CE, 0x4F97, 0x4AA0,
  ];

  function writeFormatInfo(matrix, size, maskIdx) {
    const bits = FORMAT_BITS[maskIdx];
    const positions = [
      [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [8, 7], [8, 8],
      [7, 8], [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8],
    ];
    for (let i = 0; i < 15; i++) {
      const val = (bits >> (14 - i)) & 1 ? 1 : -1;
      const [r, c] = positions[i];
      matrix[r][c] = val;
    }

    const positions2 = [
      [size - 1, 8], [size - 2, 8], [size - 3, 8], [size - 4, 8],
      [size - 5, 8], [size - 6, 8], [size - 7, 8],
      [8, size - 8], [8, size - 7], [8, size - 6], [8, size - 5],
      [8, size - 4], [8, size - 3], [8, size - 2], [8, size - 1],
    ];
    for (let i = 0; i < 15; i++) {
      const val = (bits >> (14 - i)) & 1 ? 1 : -1;
      const [r, c] = positions2[i];
      matrix[r][c] = val;
    }
  }

  function penalty(matrix, size) {
    let score = 0;

    for (let r = 0; r < size; r++) {
      let run = 1;
      for (let c = 1; c < size; c++) {
        if (matrix[r][c] === matrix[r][c - 1]) { run++; }
        else { if (run >= 5) score += run - 2; run = 1; }
      }
      if (run >= 5) score += run - 2;
    }
    for (let c = 0; c < size; c++) {
      let run = 1;
      for (let r = 1; r < size; r++) {
        if (matrix[r][c] === matrix[r - 1][c]) { run++; }
        else { if (run >= 5) score += run - 2; run = 1; }
      }
      if (run >= 5) score += run - 2;
    }

    for (let r = 0; r < size - 1; r++)
      for (let c = 0; c < size - 1; c++)
        if (matrix[r][c] === matrix[r][c + 1] && matrix[r][c] === matrix[r + 1][c] && matrix[r][c] === matrix[r + 1][c + 1])
          score += 3;

    return score;
  }

  function generate(text) {
    const version = selectVersion(text.length);
    const data = encodeData(text, version);
    const codewords = addErrorCorrection(data, version);
    const bits = [];
    for (const byte of codewords)
      for (let i = 7; i >= 0; i--) bits.push((byte >> i) & 1);

    const { matrix, reserved, size } = createMatrix(version);
    placeData(matrix, reserved, size, bits);

    let bestMask = 0, bestPenalty = Infinity;
    for (let m = 0; m < 8; m++) {
      const masked = applyMask(matrix, reserved, size, m);
      writeFormatInfo(masked, size, m);
      const p = penalty(masked, size);
      if (p < bestPenalty) { bestPenalty = p; bestMask = m; }
    }

    const final = applyMask(matrix, reserved, size, bestMask);
    writeFormatInfo(final, size, bestMask);

    return { modules: final, size };
  }

  function toCanvas(qr, pixelSize = 8, quietZone = 4) {
    const totalSize = (qr.size + quietZone * 2) * pixelSize;
    const canvas = document.createElement('canvas');
    canvas.width = totalSize;
    canvas.height = totalSize;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, totalSize, totalSize);

    ctx.fillStyle = '#000000';
    for (let r = 0; r < qr.size; r++)
      for (let c = 0; c < qr.size; c++)
        if (qr.modules[r][c] === 1)
          ctx.fillRect((c + quietZone) * pixelSize, (r + quietZone) * pixelSize, pixelSize, pixelSize);

    return canvas;
  }

  return { generate, toCanvas };
})();


// ── SmokeyWallet Custom Element ──
class SmokeyWallet extends HTMLElement {
  constructor() {
    super();
    this._drawer = null;
    this._content = null;
    this._data = null;
    this._dataTimestamp = 0;
    this._prefetchPromise = null;
    this._qrCacheUrl = null;
    this._qrCacheDataUrl = null;
    this._selectedPoints = null;
    this._previousFocus = null;
    this._boundEsc = this._onEsc.bind(this);
  }

  connectedCallback() {
    this._drawer = this.querySelector('#WalletDrawer');
    this._content = this.querySelector('#WalletDrawerContent');

    this.querySelectorAll('[data-wallet-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    this._bindTriggers();

    // Auto-open: cookie set by wallet login button (survives cross-domain login flow)
    if (/(?:^|;\s*)smk_wallet_next=1/.test(document.cookie)) {
      document.cookie = 'smk_wallet_next=;path=/;max-age=0;samesite=lax';
      this.open();
    }
    // Auto-open: explicit attribute in DOM
    else if (document.querySelector('[data-smokeys-wallet-autoopen]')) {
      this.open();
    }
  }

  _bindTriggers() {
    document.querySelectorAll('[data-smokeys-wallet-open]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
      // Prefetch on hover/touch — API call starts before click lands
      el.addEventListener('mouseenter', () => this._prefetch(), { once: true });
      el.addEventListener('touchstart', () => this._prefetch(), { once: true, passive: true });
    });
  }

  // Shared fetch logic — single source of truth for API call + response parsing
  _startFetch() {
    return fetch('/apps/ai-rails/wallet.json').then(async (resp) => {
      if (resp.status === 401) return { _walletStatus: 'login' };
      if (resp.status === 404) return { _walletStatus: 'not_found' };
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const data = await resp.json();
      data._walletStatus = 'ok';
      return data;
    });
  }

  _prefetch() {
    if (this._prefetchPromise) return;
    this._prefetchPromise = this._startFetch().catch(() => null);
    // Expire prefetch after 30s — stale hover shouldn't serve old data
    setTimeout(() => { this._prefetchPromise = null; }, 30000);
  }

  open() {
    if (!this._drawer) return;
    this._previousFocus = document.activeElement;
    this._drawer.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._boundEsc);

    // Cache hit: render instantly if data is fresh (<60s), skip skeleton flicker
    if (this._data && (Date.now() - this._dataTimestamp) < 60000) {
      this._render(this._data);
      return;
    }

    this._renderSkeleton();
    this._fetchData();
  }

  close() {
    if (!this._drawer) return;
    this._drawer.classList.remove('active');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._boundEsc);
    if (this._previousFocus) {
      this._previousFocus.focus();
      this._previousFocus = null;
    }
  }

  _onEsc(e) {
    if (e.key === 'Escape') this.close();
  }

  async _fetchData() {
    try {
      // Reuse prefetch if available (hover/touch started it early), otherwise fresh call
      const result = await (this._prefetchPromise || this._startFetch());
      this._prefetchPromise = null;

      if (!result) throw new Error('Fetch failed');
      if (result._walletStatus === 'login') { this._renderLogin(); return; }
      if (result._walletStatus === 'not_found') { this._renderNotFound(); return; }

      this._data = result;
      this._dataTimestamp = Date.now();
      this._render(result);
    } catch (err) {
      this._content.innerHTML =
        '<div class="smk-state">' +
          '<p class="smk-state__title">Connection Error</p>' +
          '<p class="smk-state__text">Couldn\'t load your wallet. Please try again.</p>' +
          '<button class="smk-btn smk-btn--secondary" onclick="document.querySelector(\'smokeys-wallet\')?.close()">Close</button>' +
        '</div>';
    }
  }

  _renderSkeleton() {
    this._content.innerHTML =
      '<div class="smk-skeleton">' +
        '<div class="smk-skeleton__block smk-skeleton__block--hero"></div>' +
        '<div class="smk-skeleton__block smk-skeleton__block--card"></div>' +
        '<div class="smk-skeleton__block smk-skeleton__block--card"></div>' +
        '<div class="smk-skeleton__block smk-skeleton__block--qr"></div>' +
      '</div>';
  }

  _renderLogin() {
    this._content.innerHTML =
      '<div class="smk-state">' +
        '<p class="smk-state__title">Log in to view your wallet</p>' +
        '<p class="smk-state__text">Access your perks, store credit, and wallet QR.</p>' +
        '<a class="smk-btn smk-btn--login" href="/customer_authentication/login?return_to=%2Fpages%2Fwallet-redirect" onclick="document.cookie=\'smk_wallet_next=1;path=/;max-age=300;samesite=lax\'">Log In</a>' +
      '</div>';
  }

  _renderNotFound() {
    this._content.innerHTML =
      '<div class="smk-state">' +
        '<p class="smk-state__title">Welcome!</p>' +
        '<p class="smk-state__text">Your wallet will be created after your first purchase. Check back soon!</p>' +
      '</div>';
  }

  _render(d) {
    var b = d.balances || {};
    var c = d.customer || {};
    var offer = d.offer || {};
    var wb = d.walletButtons || {};

    var html = '';

    // 1. Balance Card (store credit + points in one)
    html +=
      '<div class="smk-card smk-card--hero">' +
        '<p class="smk-card__eyebrow">Smoke Credit</p>' +
        '<p class="smk-card__amount">' + (b.storeCreditFormatted || '$0.00') + '</p>' +
        this._stalenessText(b.storeCreditLastSyncedAt) +
        '<span class="smk-card__credit-tip">' +
          '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>' +
          'Select Smoke Credit at checkout to use' +
        '</span>' +
        '<div class="smk-card__divider"></div>' +
        '<div class="smk-card__points-row">' +
          '<div class="smk-card__points-left">' +
            '<span class="smk-card__value">' + (b.availablePoints != null ? b.availablePoints : b.points || 0).toLocaleString() + '</span>' +
            '<span class="smk-card__label">Smoke Points</span>' +
          '</div>' +
          '<div class="smk-card__points-right">' +
            '<span class="smk-card__equiv">' + (b.availableDollars ? '$' + b.availableDollars : '$0.00') + '</span>' +
            '<span class="smk-pill">Smoke Perks</span>' +
          '</div>' +
        '</div>' +
      '</div>';

    // 3. Offer Banner
    if (offer.title) {
      html +=
        '<div class="smk-card smk-card--offer">' +
          '<p class="smk-card__offer-title">' + this._esc(offer.title) + '</p>' +
          (offer.body ? '<p class="smk-card__offer-body">' + this._esc(offer.body) + '</p>' : '') +
        '</div>';
    }

    // 4. QR Membership Card + Wallet Buttons
    if (c.qrUrl) {
      var phone = c.phone || '';
      html +=
        '<div class="smk-card smk-card--qr">' +
          '<div class="smk-card__qr-wrap" id="wallet-qr-target"></div>' +
          '<p class="smk-card__qr-label">Show this QR at checkout to earn &amp; redeem</p>' +
          (phone ? '<div class="smk-card__qr-fallback">' +
            '<button class="smk-card__qr-fallback-btn" type="button" data-wallet-show-code>Can\'t scan?</button>' +
            '<div class="smk-card__qr-fallback-detail" id="wallet-fallback-code">' +
              '<p class="smk-card__qr-fallback-hint">Give the cashier your phone number to look you up</p>' +
              '<p class="smk-card__qr-fallback-code">' + this._esc(phone) + '</p>' +
            '</div>' +
          '</div>' : '') +
        '</div>';

      html += '<div class="smk-passes">';
      if (wb.applePass) {
        html +=
          '<a class="smk-pass-btn" href="' + this._esc(wb.applePass) + '">' +
            '<svg width="20" height="24" viewBox="0 0 20 24" fill="currentColor"><path d="M17.05 12.54c-.03-3.02 2.46-4.47 2.57-4.54-1.4-2.05-3.58-2.33-4.36-2.36-1.86-.19-3.63 1.1-4.57 1.1-.94 0-2.4-1.07-3.94-1.04-2.03.03-3.9 1.18-4.94 3-2.11 3.66-.54 9.08 1.52 12.05 1 1.45 2.2 3.08 3.77 3.02 1.52-.06 2.09-.98 3.93-.98 1.83 0 2.35.98 3.96.95 1.63-.03 2.66-1.48 3.65-2.93 1.15-1.68 1.62-3.31 1.65-3.4-.04-.01-3.17-1.22-3.2-4.83z"/><path d="M14.03 3.54c.83-1.01 1.4-2.42 1.24-3.82-1.2.05-2.66.8-3.52 1.81-.77.89-1.45 2.31-1.27 3.68 1.34.1 2.71-.68 3.55-1.67z"/></svg>' +
            'Add to Apple Wallet' +
          '</a>';
      }
      if (SMK_ENABLE_GOOGLE_WALLET && wb.googlePass) {
        html +=
          '<a class="smk-pass-btn" href="' + this._esc(wb.googlePass) + '">' +
            '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.586 14.424l-1.188 1.188a.5.5 0 01-.707 0L10 12.92l-2.691 2.691a.5.5 0 01-.707 0l-1.188-1.188a.5.5 0 010-.707L8.105 11 5.414 8.309a.5.5 0 010-.707l1.188-1.188a.5.5 0 01.707 0L10 9.105l2.691-2.691a.5.5 0 01.707 0l1.188 1.188a.5.5 0 010 .707L11.895 11l2.691 2.691a.5.5 0 010 .733z"/></svg>' +
            'Add to Google Wallet' +
          '</a>';
      } else if (!SMK_ENABLE_GOOGLE_WALLET && wb.googlePass) {
        html += '<p class="smk-pass-unavailable">Google Wallet temporarily unavailable</p>';
      }
      html += '</div>';
    }

    // 5. Info section (collapsed)
    html +=
      '<div class="smk-info">' +
        '<button class="smk-info__toggle" type="button" data-wallet-info-toggle>' +
          'How Smoke Perks works' +
          '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="smk-info__body" id="wallet-info-body">' +
          '<p class="smk-info__item"><strong>Smoke Credit (spendable $):</strong> Smoke Credit can be used online at checkout on getsmokeys.com. To use Smoke Credit in-store, present this QR code at the time of purchase.</p>' +
          '<p class="smk-info__item"><strong>Earn Smoke Points:</strong> Smoke Points may be added through eligible loyalty activity, promotions, and special offers.</p>' +
          '<p class="smk-info__item"><strong>Value:</strong> 400 Smoke Points = $1.00 in Smoke Credit. No cash value. Not redeemable for cash.</p>' +
          '<p class="smk-info__item"><strong>Activity / Expiry:</strong> Smoke Points may expire after 90 days of inactivity. Earning or redeeming keeps your account active.</p>' +
          '<p class="smk-info__item"><strong>Timing:</strong> Updates typically appear quickly, but may be delayed briefly after checkout due to processing or connectivity.</p>' +
          '<p class="smk-info__item"><strong>Scanning tips:</strong> Increase screen brightness, hold steady, or ask staff to look up your account by phone number.</p>' +
          '<p class="smk-info__item smk-info__legal"><em>Smokey&rsquo;s Inc. may modify, suspend, or terminate Smoke Perks (including earning, expiry, eligibility, redemption, and conversion rates) at any time, without notice where permitted by law.</em></p>' +
          '<p class="smk-info__item">Need help? <a href="/pages/contact">Contact Support</a></p>' +
        '</div>' +
      '</div>';

    // 6. Reissue
    if (c.hasToken !== false && c.qrUrl) {
      var flags = d.flags || {};
      var cooldownUntil = flags.reissueCooldownUntil ? new Date(flags.reissueCooldownUntil) : null;
      var onCooldown = cooldownUntil && cooldownUntil > new Date();
      var cooldownLabel = onCooldown ? cooldownUntil.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

      html +=
        '<div class="smk-reissue">' +
          '<button class="smk-reissue__btn" type="button" data-wallet-reissue' + (onCooldown ? ' disabled' : '') + '>' +
            (onCooldown ? 'Available ' + this._esc(cooldownLabel) : 'Reissue Card') +
          '</button>' +
          '<p class="smk-reissue__hint">' +
            (onCooldown
              ? 'Card reissue is limited to once every 30 days.'
              : 'Lost your phone? Get a new QR code. Old one stops working immediately.') +
          '</p>' +
        '</div>';
    }

    this._content.innerHTML = html;

    // Generate QR (cached — only regenerated when URL changes, e.g. after reissue)
    if (c.qrUrl) {
      var target = this._content.querySelector('#wallet-qr-target');
      if (target) {
        if (this._qrCacheUrl === c.qrUrl && this._qrCacheDataUrl) {
          var img = document.createElement('img');
          img.src = this._qrCacheDataUrl;
          img.width = 140;
          img.height = 140;
          img.alt = 'Wallet QR code';
          target.appendChild(img);
        } else {
          try {
            var qrResult = QR.generate(c.qrUrl);
            var canvas = QR.toCanvas(qrResult, 6, 3);
            canvas.style.width = '140px';
            canvas.style.height = '140px';
            this._qrCacheUrl = c.qrUrl;
            this._qrCacheDataUrl = canvas.toDataURL();
            target.appendChild(canvas);
          } catch (qrErr) {
            target.innerHTML = '<p style="color:#999;font-size:12px;">QR unavailable</p>';
          }
        }
      }
    }

    this._bindRenderEvents();
  }

  _bindRenderEvents() {
    var self = this;

    // QR fallback toggle
    var showCodeBtn = this._content.querySelector('[data-wallet-show-code]');
    if (showCodeBtn) {
      showCodeBtn.addEventListener('click', function() {
        var code = self._content.querySelector('#wallet-fallback-code');
        if (code) code.classList.toggle('visible');
      });
    }

    // Info toggle
    var infoToggle = this._content.querySelector('[data-wallet-info-toggle]');
    if (infoToggle) {
      infoToggle.addEventListener('click', function() {
        infoToggle.classList.toggle('open');
        var body = self._content.querySelector('#wallet-info-body');
        if (body) body.classList.toggle('visible');
      });
    }

    // Reissue
    var reissueBtn = this._content.querySelector('[data-wallet-reissue]');
    if (reissueBtn) {
      reissueBtn.addEventListener('click', function() { self._handleReissue(reissueBtn); });
    }

  }

  async _handleReissue(btn) {
    if (!confirm('Reissue your wallet card? Your old QR will stop working immediately.')) return;

    btn.disabled = true;
    btn.textContent = 'Reissuing...';

    try {
      var resp = await fetch('/apps/ai-rails/wallet.json?action=reissue');
      if (resp.status === 429) {
        var body = await resp.json().catch(function() { return {}; });
        var until = body.cooldownUntil ? new Date(body.cooldownUntil).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '30 days';
        btn.disabled = true;
        btn.textContent = 'Available ' + until;
        this._showToast('Reissue limited to once every 30 days.', 'error');
        return;
      }
      if (!resp.ok) throw new Error('Reissue failed');

      var freshResp = await fetch('/apps/ai-rails/wallet.json');
      if (freshResp.ok) {
        this._data = await freshResp.json();
        this._dataTimestamp = Date.now();
        this._qrCacheUrl = null;       // Invalidate QR cache — new card issued
        this._qrCacheDataUrl = null;
        this._render(this._data);
      }
      this._showToast('Wallet card reissued successfully!', 'success');
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Reissue Card';
      this._showToast('Reissue failed. Please try again.', 'error');
    }
  }

  _stalenessText(isoStr) {
    if (!isoStr) return '';
    var diff = Date.now() - new Date(isoStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 5) return '';
    if (mins < 60) return '<p class="smk-card__stale">Updated ' + mins + ' min ago</p>';
    var hrs = Math.floor(mins / 60);
    if (hrs < 24) return '<p class="smk-card__stale">Updated ' + hrs + 'h ago</p>';
    return '<p class="smk-card__stale">Updated ' + Math.floor(hrs / 24) + 'd ago</p>';
  }

  _showToast(message, type) {
    type = type || 'success';
    document.querySelectorAll('.smk-wallet__toast').forEach(function(t) { t.remove(); });

    var toast = document.createElement('div');
    toast.className = 'smk-wallet__toast ' + type;
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(function() {
      requestAnimationFrame(function() { toast.classList.add('visible'); });
    });

    setTimeout(function() {
      toast.classList.remove('visible');
      setTimeout(function() { toast.remove(); }, 300);
    }, 3000);
  }

  _esc(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }
}

customElements.define('smokeys-wallet', SmokeyWallet);

// Global wallet open handler
window.SmokeysWallet = window.SmokeysWallet || {};
window.SmokeysWallet.open = function() {
  var w = document.querySelector('smokeys-wallet');
  if (w && typeof w.open === 'function') return w.open();
  var btn = document.querySelector('[data-smokeys-wallet-open]');
  if (btn) btn.click();
};
document.addEventListener('smokeys:wallet:open', function() { window.SmokeysWallet.open(); });
